import {
  Auth,
  EmailAuthProvider,
  MultiFactorInfo,
  MultiFactorResolver,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
  User,
  multiFactor,
  reauthenticateWithCredential,
} from "firebase/auth";

const ENROLL_KEY = "superAdminSmsEnroll";

export function beginSmsEnrollmentSession() {
  sessionStorage.setItem(ENROLL_KEY, "1");
}

export function endSmsEnrollmentSession() {
  sessionStorage.removeItem(ENROLL_KEY);
}

export function smsEnrollmentSessionActive() {
  try {
    return sessionStorage.getItem(ENROLL_KEY) === "1";
  } catch (error) {
    return false;
  }
}

export async function reauthenticateForSms(user: User, password: string) {
  const email = user.email || "";
  if (!email || !password) {
    throw new Error("Enter your current password before sending the text code.");
  }
  const credential = EmailAuthProvider.credential(email, password);
  await reauthenticateWithCredential(user, credential);
}

export function phoneEnrolled(user: User | null | undefined) {
  if (!user) return false;
  return multiFactor(user).enrolledFactors.some((factor) => factor.factorId === "phone");
}

export function enrolledPhone(user: User | null | undefined) {
  if (!user) return "";
  const factor = multiFactor(user).enrolledFactors.find((item) => item.factorId === "phone");
  if (factor && "phoneNumber" in factor && factor.phoneNumber) return String(factor.phoneNumber);
  return "";
}

export function smsErrorMessage(error: unknown) {
  const code = error && typeof error === "object" && "code" in error ? String((error as { code?: string }).code) : "";
  if (code === "auth/requires-recent-login") return "Sign out and sign in again, then try this step.";
  if (code === "auth/invalid-verification-code" || code === "auth/code-expired") {
    return "That code was not accepted. Request a new one and try again.";
  }
  if (code === "auth/invalid-phone-number") {
    return "Enter the phone number with the country code, for example +15145551234.";
  }
  if (code === "auth/wrong-password" || code === "auth/invalid-credential" || code === "auth/invalid-login-credentials") {
    return "Current password was not accepted.";
  }
  if (code === "auth/unsupported-first-factor") {
    return "Sign in again with your password, then turn on text message.";
  }
  if (error && typeof error === "object" && "message" in error) {
    const message = String((error as { message?: string }).message || "");
    if (message) return message;
  }
  return "Could not finish the text-message step.";
}

const verifiers = new Map<string, RecaptchaVerifier>();

function freshRecaptcha(auth: Auth, elementId: string) {
  const existing = verifiers.get(elementId);
  if (existing) return existing;
  const host = document.getElementById(elementId);
  if (!host) throw new Error("Reload the page, then send the code again.");
  const verifier = new RecaptchaVerifier(elementId, { size: "invisible" }, auth);
  verifiers.set(elementId, verifier);
  return verifier;
}

export async function sendEnrollmentSms(auth: Auth, user: User, phoneNumber: string, elementId: string) {
  const session = await multiFactor(user).getSession();
  return new PhoneAuthProvider(auth).verifyPhoneNumber({ phoneNumber, session }, freshRecaptcha(auth, elementId));
}

export async function confirmEnrollmentSms(user: User, verificationId: string, code: string) {
  const credential = PhoneAuthProvider.credential(verificationId, code);
  await multiFactor(user).enroll(PhoneMultiFactorGenerator.assertion(credential), "Text message");
}

export async function unenrollSms(user: User) {
  const factor = multiFactor(user).enrolledFactors.find((item) => item.factorId === "phone");
  if (!factor) return;
  await multiFactor(user).unenroll(factor);
}

export async function sendSignInSms(auth: Auth, resolver: MultiFactorResolver, hint: MultiFactorInfo, elementId: string) {
  return new PhoneAuthProvider(auth).verifyPhoneNumber(
    { multiFactorHint: hint, session: resolver.session },
    freshRecaptcha(auth, elementId)
  );
}

export async function finishSmsSignIn(resolver: MultiFactorResolver, verificationId: string, code: string) {
  const credential = PhoneAuthProvider.credential(verificationId, code);
  return resolver.resolveSignIn(PhoneMultiFactorGenerator.assertion(credential));
}
